import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, User, Loader2, ArrowRight } from 'lucide-react';
import { RelatedArticles } from '@/components/RelatedArticles';
import { API } from '@/lib/api';

const SITE_URL = 'https://crossfit-saintchamond.fr';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(data);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        else setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return <Navigate to="/blog" replace />;
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  const metaDesc = post.meta_description || post.excerpt;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: metaDesc,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: {
      '@type': 'Person',
      name: post.author || 'Nicolas Perroy',
      jobTitle: 'Coach CrossFit certifié & Fondateur CrossFit Saint-Chamond',
      url: `${SITE_URL}/about`,
      worksFor: {
        '@type': 'Organization',
        name: 'CrossFit Saint-Chamond',
        url: SITE_URL,
      },
      sameAs: [
        'https://www.instagram.com/crossfit_saintchamond',
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'CrossFit Saint-Chamond',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo192.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
    keywords: (post.tags || []).join(', '),
  };

  return (
    <div className="min-h-screen">
      <PageMeta title={post.title} description={metaDesc} path={`/blog/${post.slug}`} image={post.cover_image} />
      <Helmet>
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:modified_time" content={post.updated_at} />
        <meta property="article:author" content={post.author || 'Nicolas Perroy'} />
        {(post.tags || []).map((t) => <meta key={t} property="article:tag" content={t} />)}
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>
      <BreadcrumbJsonLd items={[
        { name: 'Accueil', url: '/' },
        { name: 'Blog', url: '/blog' },
        { name: post.title },
      ]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <article className="relative" data-testid="blog-post">
        {/* Hero */}
        <section className="relative bg-dark overflow-hidden">
          {post.cover_image && (
            <div className="absolute inset-0">
              <img loading="lazy" decoding="async" src={post.cover_image} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30 ken-burns" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-dark via-dark/92 to-primary-dark/40" />
          <div className="absolute inset-0 mesh-gradient opacity-40 pointer-events-none" />

          <div className="container-custom relative z-10 py-20 md:py-28">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-primary-glow text-sm mb-6 transition-colors" data-testid="blog-back-link">
              <ArrowLeft className="h-4 w-4" /> Retour au blog
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              {(post.tags || []).map((t) => (
                <Badge key={t} className="bg-primary/20 hover:bg-primary/25 text-primary-glow border-primary-glow/30 text-[10px] tracking-wider">
                  {t}
                </Badge>
              ))}
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white leading-[1.05] tracking-tight mb-6 max-w-4xl">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-white/60 text-xs tracking-widest uppercase">
              <span className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-primary-glow" />
                {post.author || 'Nicolas Perroy'}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-primary-glow" />
                {formatDate(post.created_at)}
              </span>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="section-padding bg-background">
          <div className="container-custom max-w-3xl">
            <p className="text-lg text-muted-foreground leading-relaxed italic border-l-4 border-primary pl-5 mb-10" data-testid="blog-excerpt">
              {post.excerpt}
            </p>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:tracking-wide prose-headings:text-dark
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-tight
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-foreground prose-p:leading-[1.75]
                prose-strong:text-dark
                prose-a:text-primary prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
                prose-ul:my-4 prose-li:my-1
                prose-blockquote:border-l-primary prose-blockquote:bg-primary/5 prose-blockquote:py-2 prose-blockquote:px-5 prose-blockquote:not-italic prose-blockquote:rounded-r-lg
                prose-table:text-sm prose-th:bg-primary prose-th:text-white prose-th:p-3
                prose-td:border prose-td:p-3
                prose-hr:my-10 prose-hr:border-border
                prose-img:rounded-2xl"
              data-testid="blog-content"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
            </div>

            {/* CTA end-of-article */}
            <div className="mt-14 p-8 md:p-10 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 text-center">
              <h3 className="font-heading text-2xl md:text-3xl text-dark mb-3 tracking-wide">
                ENVIE DE <span className="text-gradient-primary italic font-display inline-block pr-2">T'ENTRAÎNER</span> AVEC NOUS ?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Viens essayer un cours gratuitement à CrossFit Saint-Chamond. Coaching pro, ambiance au top.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact">
                  <Button className="btn-shine bg-primary hover:bg-primary-light rounded-xl glow-primary px-8 py-6" size="lg">
                    ESSAI GRATUIT
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/blog">
                  <Button variant="outline" className="rounded-xl px-8 py-6" size="lg">
                    Voir tous les articles
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related */}
        <RelatedArticles
          keywords={[...(post.tags || []), ...(post.title || '').split(' ').filter(w => w.length > 4).slice(0, 3)]}
          excludeSlug={post.slug}
          title="À lire également"
          limit={3}
        />
      </article>

      <Footer />
    </div>
  );
}
